import { AuthApiError } from "@supabase/supabase-js";
import { supabase } from "@/api/supabase";

/**
 * Typed auth errors, mirroring the Flutter app's error hierarchy
 * (`auth_remote_data_source.dart`) — callers key UI copy off the error
 * type, never off a raw Supabase message.
 */
export class InvalidCredentialsError extends Error {
  constructor() {
    super("Invalid email or password.");
    this.name = "InvalidCredentialsError";
  }
}

export class InvalidOtpError extends Error {
  constructor() {
    super("That code is invalid or has expired.");
    this.name = "InvalidOtpError";
  }
}

/**
 * `resolve_sign_in` RPC — decides the sign-in path: `true` means the email
 * belongs to an administrator, company or driver of the tenant (one-time
 * email code, no password); `false` means everyone else (password sign-in:
 * the platform's super admins on the console, where `tenantSlug` is `null`).
 */
export async function isOtpSignIn(
  email: string,
  tenantSlug: string | null,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("resolve_sign_in", {
    p_email: email,
    ...(tenantSlug === null ? {} : { p_tenant_slug: tenantSlug }),
  });
  if (error) {
    throw error;
  }
  return data === "otp";
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) {
    if (error instanceof AuthApiError && error.code === "invalid_credentials") {
      throw new InvalidCredentialsError();
    }
    throw error;
  }
}

/**
 * Sends the one-time code. `tenantSlug` is stored on the new auth user and
 * tells the sign-up trigger which tenant's company/driver list to check the
 * email against; it can't grant access by itself.
 */
export async function signInWithOtp(
  email: string,
  tenantSlug: string,
): Promise<void> {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { data: { tenant_slug: tenantSlug } },
  });
  if (error) {
    throw error;
  }
}

export async function verifyOtp(email: string, token: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({
    email,
    token,
    type: "email",
  });
  if (error) {
    if (error instanceof AuthApiError) {
      throw new InvalidOtpError();
    }
    throw error;
  }
}

/**
 * Super-admin only. Everyone who signs in on an organization's address uses
 * a one-time code and has no password — callers must check `isOtpSignIn`
 * first and refuse the request client-side for those emails, mirroring
 * `PasswordResetNotAdminError` in the Flutter app.
 */
export async function resetPasswordForEmail(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/update-password`,
  });
  if (error) {
    throw error;
  }
}

export async function updatePassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) {
    throw error;
  }
}

export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) {
    throw error;
  }
}
